/**
 * Shell Game Timeline Modal
 *
 * Displays complete carrier switch timeline for a VIN showing fraud patterns.
 * Visual timeline with carrier switches and evidence summary.
 */

import React, { useEffect, useState } from 'react';
import { X, AlertTriangle, TrendingUp, Calendar, MapPin, XCircle, CheckCircle, Shield } from 'lucide-react';
import { LoadingSpinner, ErrorState } from './shared';
import { carrierApi } from '../services/api';
import '../styles/modern.css';

/**
 * Type definition for inspection data passed to viewer
 */
interface Inspection {
  inspection_id?: string;
  report_number?: string;
  insp_date?: string;
  dot_number?: number;
  carrier_name?: string;
  viol_total?: number;
  oos_total?: number;
  insp_level_id?: number;
  [key: string]: unknown;
}

interface CarrierSwitch {
  fromDotNumber: number;
  fromCarrierName: string;
  lastSeenAt: string;
  toDotNumber: number;
  toCarrierName: string;
  firstSeenAt: string;
  daysBetween: number;
  suspicious: boolean;
  suspicionReasons: string[];
}

interface TimelineEvent {
  vin: string;
  dotNumber: number;
  carrierName: string;
  shipperName?: string;
  shippingPaperNumber?: string;
  eventType: 'crash' | 'inspection';
  eventDate: string;
  hadViolations?: boolean;
  hadCrash?: boolean;
  hadOOS?: boolean;
  violTotal?: number;
  oosTotal?: number;
  driverOosTotal?: number;
  vehicleOosTotal?: number;
  location?: string;
  locationDesc?: string;
  reportState?: string;
  inspectionLevel?: number;
  reportNumber?: string;
  inspectionId?: string;
}

interface TimelineData {
  vin: string;
  totalEvents: number;
  uniqueCarriers: number;
  switchCount: number;
  suspiciousSwitches: number;
  switches: CarrierSwitch[];
  timeline: TimelineEvent[];
  riskScore: number;
  riskLevel: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  fraudIndicators: string[];
}

interface ShellGameTimelineModalProps {
  vin: string;
  onClose: () => void;
  onNavigateToCarrier?: (dotNumber: number) => void;
  onViewInspection?: (inspection: Inspection) => void;
}

export const ShellGameTimelineModal: React.FC<ShellGameTimelineModalProps> = ({ vin, onClose, onNavigateToCarrier, onViewInspection }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TimelineData | null>(null);

  const fetchTimeline = async () => {
    try {
      setLoading(true);
      setError(null);

      const result = await carrierApi.getVINTimeline(vin);

      // Handle both wrapped and unwrapped response formats
      const timelineData = (result.data || result) as TimelineData;
      setData(timelineData);
    } catch (err: unknown) {
      console.error('Error fetching VIN timeline:', err);
      const errorMessage = err instanceof Error
        ? (err as any).response?.data?.message || err.message
        : 'Failed to load timeline data';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTimeline();
  }, [vin]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'CRITICAL':
        return 'var(--color-danger)';
      case 'HIGH':
        return '#f97316';
      case 'MEDIUM':
        return 'var(--color-warning)';
      default:
        return 'var(--color-success)';
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 'var(--space-lg)',
      }}
      onClick={onClose}
    >
      <div
        className="card-modern"
        style={{
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'white',
          zIndex: 10,
          paddingBottom: 'var(--space-lg)',
          borderBottom: '1px solid var(--color-gray-200)',
          marginBottom: 'var(--space-xl)',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <h2 className="heading-2" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)' }}>
                <Shield style={{ width: 28, height: 28, color: 'var(--color-primary)' }} />
                VIN Fraud Timeline Analysis
              </h2>
              <code style={{
                fontFamily: 'monospace',
                fontSize: '1rem',
                color: 'var(--color-gray-700)',
                background: 'var(--color-gray-100)',
                padding: '0.5rem 0.75rem',
                borderRadius: 'var(--radius-md)',
                display: 'inline-block',
              }}>
                {vin}
              </code>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: 'var(--space-sm)',
                color: 'var(--color-gray-400)',
              }}
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ textAlign: 'center', padding: 'var(--space-3xl)' }}>
            <LoadingSpinner message="Loading timeline..." size="lg" />
            <p style={{ marginTop: 'var(--space-lg)', color: 'var(--color-gray-500)' }}>
              Analyzing VIN history across 9.3M records...
            </p>
          </div>
        )}

        {error && (
          <ErrorState
            title="Timeline Data Unavailable"
            message={error}
            onRetry={fetchTimeline}
            variant="inline"
            icon="error"
          />
        )}

        {!loading && !error && data && (
          <>
            {/* Risk Assessment Card */}
            <div
              className="card-modern"
              style={{
                borderLeft: `4px solid ${getRiskColor(data.riskLevel)}`,
                marginBottom: 'var(--space-xl)',
              }}
            >
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 'var(--space-md)' }}>
                <div className="stat-box" style={{ padding: 'var(--space-sm)' }}>
                  <div className="stat-value" style={{ fontSize: '1.5rem', color: getRiskColor(data.riskLevel) }}>
                    {data.riskScore}
                  </div>
                  <div className="stat-label" style={{ fontSize: '0.7rem' }}>Fraud Risk Score</div>
                  <div className={`badge-modern badge-${data.riskLevel === 'CRITICAL' ? 'danger' : data.riskLevel === 'HIGH' ? 'warning' : 'success'}`} style={{ marginTop: 'var(--space-xs)', fontSize: '0.7rem', padding: '2px 8px' }}>
                    {data.riskLevel}
                  </div>
                </div>

                <div className="stat-box" style={{ padding: 'var(--space-sm)' }}>
                  <div className="stat-value" style={{ fontSize: '1.5rem' }}>{data.uniqueCarriers}</div>
                  <div className="stat-label" style={{ fontSize: '0.7rem' }}>Carriers Detected</div>
                </div>

                <div className="stat-box" style={{ padding: 'var(--space-sm)' }}>
                  <div className="stat-value" style={{ fontSize: '1.5rem', color: data.switchCount > 2 ? 'var(--color-danger)' : 'var(--color-gray-700)' }}>
                    {data.switchCount}
                  </div>
                  <div className="stat-label" style={{ fontSize: '0.7rem' }}>Carrier Switches</div>
                </div>

                <div className="stat-box" style={{ padding: 'var(--space-sm)' }}>
                  <div className="stat-value" style={{ fontSize: '1.5rem' }}>{data.totalEvents}</div>
                  <div className="stat-label" style={{ fontSize: '0.7rem' }}>Total Events</div>
                </div>
              </div>

              {data.fraudIndicators.length > 0 && (
                <div style={{
                  marginTop: 'var(--space-lg)',
                  padding: 'var(--space-md)',
                  background: '#fef2f2',
                  borderRadius: 'var(--radius-md)',
                }}>
                  <h4 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-danger)', marginBottom: 'var(--space-sm)' }}>
                    🚨 Fraud Indicators Detected:
                  </h4>
                  <ul style={{ margin: 0, paddingLeft: 'var(--space-lg)', fontSize: '0.8125rem', color: 'var(--color-gray-700)' }}>
                    {data.fraudIndicators.map((indicator, idx) => (
                      <li key={idx} style={{ marginBottom: '0.25rem' }}>{indicator}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Timeline Visualization */}
            <div className="card-modern" style={{ marginBottom: 'var(--space-xl)' }}>
              <h3 className="heading-3" style={{ marginBottom: 'var(--space-xl)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <Calendar style={{ width: 20, height: 20, color: 'var(--color-primary)' }} />
                Carrier Switch Timeline
              </h3>

              <div style={{ position: 'relative', paddingLeft: '2rem' }}>
                {/* Vertical timeline line */}
                <div style={{
                  position: 'absolute',
                  left: '0.875rem',
                  top: '1rem',
                  bottom: '1rem',
                  width: '2px',
                  background: 'var(--color-gray-200)',
                }} />

                {/* Timeline events */}
                {data.timeline.map((event, idx) => {
                  const isViolation = event.hadViolations || event.hadCrash || event.hadOOS;
                  const isSwitch = idx > 0 && event.dotNumber !== data.timeline[idx - 1].dotNumber;

                  return (
                    <div key={idx} style={{ position: 'relative', marginBottom: 'var(--space-xl)' }}>
                      {/* Timeline dot */}
                      <div style={{
                        position: 'absolute',
                        left: '-1.625rem',
                        top: '0.5rem',
                        width: '1.5rem',
                        height: '1.5rem',
                        borderRadius: '50%',
                        background: isViolation ? 'var(--color-danger)' : 'var(--color-success)',
                        border: '3px solid white',
                        boxShadow: '0 0 0 2px var(--color-gray-200)',
                        zIndex: 2,
                      }} />

                      {/* Switch indicator */}
                      {isSwitch && (
                        <div style={{
                          position: 'absolute',
                          left: '-1.25rem',
                          top: '-1.5rem',
                          padding: '0.25rem 0.5rem',
                          background: 'var(--color-warning)',
                          color: 'white',
                          fontSize: '0.6875rem',
                          fontWeight: 600,
                          borderRadius: 'var(--radius-sm)',
                          boxShadow: 'var(--shadow-sm)',
                        }}>
                          SWITCH
                        </div>
                      )}

                      {/* Event card */}
                      <div className="card-modern-compact" style={{
                        borderLeft: isViolation ? '3px solid var(--color-danger)' : '3px solid var(--color-success)',
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--space-sm)' }}>
                          <div>
                            {onNavigateToCarrier ? (
                              <button
                                onClick={() => {
                                  onNavigateToCarrier(event.dotNumber);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: 'var(--color-primary)',
                                  textDecoration: 'underline',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: 600,
                                  padding: 0,
                                  font: 'inherit',
                                  marginBottom: '0.25rem',
                                  display: 'block',
                                  textAlign: 'left',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.color = 'var(--color-primary-dark)';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.color = 'var(--color-primary)';
                                }}
                              >
                                {event.carrierName}
                              </button>
                            ) : (
                              <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)', marginBottom: '0.25rem' }}>
                                {event.carrierName}
                              </div>
                            )}
                            <code style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)', fontFamily: 'monospace' }}>
                              DOT {event.dotNumber}
                            </code>
                          </div>
                          <div style={{ textAlign: 'right' }}>
                            <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-900)' }}>
                              {formatDate(event.eventDate)}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                              {event.eventType === 'crash' ? 'Crash' : `Level ${event.inspectionLevel || '?'} Inspection`}
                            </div>
                          </div>
                        </div>

                        {/* Event status */}
                        <div style={{ display: 'flex', gap: 'var(--space-sm)', flexWrap: 'wrap', marginBottom: 'var(--space-sm)' }}>
                          {event.hadCrash && (
                            <span className="badge-modern badge-danger" style={{ fontSize: '0.75rem' }}>
                              CRASH
                            </span>
                          )}
                          {event.hadViolations && (
                            <span className="badge-modern badge-danger" style={{ fontSize: '0.75rem' }}>
                              VIOLATIONS
                            </span>
                          )}
                          {event.hadOOS && (
                            <span className="badge-modern badge-danger" style={{ fontSize: '0.75rem' }}>
                              OUT-OF-SERVICE
                            </span>
                          )}
                          {!event.hadViolations && event.eventType === 'inspection' && (
                            <span className="badge-modern badge-success" style={{ fontSize: '0.75rem' }}>
                              ✓ CLEAN INSPECTION
                            </span>
                          )}
                        </div>

                        {/* Inspection Number (clickable) - Use inspection_id like shipper audit */}
                        {event.eventType === 'inspection' && event.inspectionId && (
                          <div style={{ marginTop: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onViewInspection) {
                                  onViewInspection({
                                    inspectionId: event.inspectionId,        // Use camelCase for consistency
                                    inspection_id: event.inspectionId,       // Also include snake_case for backwards compat
                                    reportNumber: event.reportNumber,        // Use camelCase
                                    report_number: event.reportNumber,       // Also include snake_case
                                    inspDate: event.eventDate,               // Use camelCase
                                    insp_date: event.eventDate,              // Also include snake_case
                                    dotNumber: event.dotNumber,              // Use camelCase
                                    dot_number: event.dotNumber,             // Also include snake_case
                                    carrierName: event.carrierName,          // Use camelCase
                                    carrier_name: event.carrierName,         // Also include snake_case
                                    inspCarrierName: event.carrierName,      // Modal expects this field name
                                    insp_carrier_name: event.carrierName,    // Also snake_case version
                                    shipperName: (event as any).shipperName || '',    // SHIPPER NAME - critical field
                                    shipper_name: (event as any).shipperName || '',   // Also snake_case
                                    shippingPaperNumber: (event as any).shippingPaperNumber || '', // BOL# - critical field
                                    shipping_paper_number: (event as any).shippingPaperNumber || '', // Also snake_case
                                    violTotal: (event as any).violTotal || 0,     // Use camelCase - actual count from backend
                                    viol_total: (event as any).violTotal || 0,    // Also include snake_case
                                    oosTotal: (event as any).oosTotal || 0,       // Use camelCase - actual count from backend
                                    oos_total: (event as any).oosTotal || 0,      // Also include snake_case
                                    driverOosTotal: (event as any).driverOosTotal || 0,   // Driver OOS count
                                    driver_oos_total: (event as any).driverOosTotal || 0, // Also snake_case
                                    vehicleOosTotal: (event as any).vehicleOosTotal || 0, // Vehicle OOS count
                                    vehicle_oos_total: (event as any).vehicleOosTotal || 0, // Also snake_case
                                    inspLevelId: event.inspectionLevel,      // Use camelCase
                                    insp_level_id: event.inspectionLevel,    // Also include snake_case
                                    locationDesc: (event as any).locationDesc || '', // Add location description
                                    location_desc: (event as any).locationDesc || '', // Also snake_case
                                    reportState: (event as any).reportState || '',    // Add report state
                                    report_state: (event as any).reportState || '',   // Also snake_case
                                    state: (event as any).reportState || ''           // Alternative field name
                                  });
                                }
                              }}
                              style={{
                                background: '#eff6ff',
                                border: '1px solid #3b82f6',
                                borderRadius: '4px',
                                padding: '4px 8px',
                                fontSize: '0.7rem',
                                color: '#3b82f6',
                                cursor: 'pointer',
                                fontFamily: 'monospace',
                                fontWeight: 600
                              }}
                              title="View inspection details from our database"
                            >
                              #{event.inspectionId}
                            </button>
                          </div>
                        )}

                        {/* Location */}
                        {event.location && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', fontSize: '0.75rem', color: 'var(--color-gray-500)' }}>
                            <MapPin size={12} />
                            {event.location}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Switches Detail */}
            {data.switches.length > 0 && (
              <div className="card-modern">
                <h3 className="heading-3" style={{ marginBottom: 'var(--space-lg)', display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                  <TrendingUp style={{ width: 20, height: 20, color: 'var(--color-warning)' }} />
                  Detected Switches ({data.switches.length})
                </h3>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                  {data.switches.map((switchItem, idx) => (
                    <div
                      key={idx}
                      className="card-modern-compact"
                      style={{
                        borderLeft: switchItem.suspicious ? '3px solid var(--color-danger)' : '3px solid var(--color-gray-300)',
                      }}
                    >
                      <div style={{ marginBottom: 'var(--space-md)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)', marginBottom: 'var(--space-sm)', flexWrap: 'wrap' }}>
                          {onNavigateToCarrier ? (
                            <button
                              onClick={() => onNavigateToCarrier(switchItem.fromDotNumber)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-primary)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                padding: 0,
                                font: 'inherit',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--color-primary-dark)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--color-primary)';
                              }}
                            >
                              {switchItem.fromCarrierName}
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-700)' }}>
                              {switchItem.fromCarrierName}
                            </span>
                          )}
                          <code style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', fontFamily: 'monospace' }}>
                            DOT {switchItem.fromDotNumber}
                          </code>
                          <TrendingUp size={16} style={{ color: 'var(--color-gray-400)' }} />
                          {onNavigateToCarrier ? (
                            <button
                              onClick={() => onNavigateToCarrier(switchItem.toDotNumber)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--color-primary)',
                                textDecoration: 'underline',
                                cursor: 'pointer',
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                padding: 0,
                                font: 'inherit',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = 'var(--color-primary-dark)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = 'var(--color-primary)';
                              }}
                            >
                              {switchItem.toCarrierName}
                            </button>
                          ) : (
                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-gray-700)' }}>
                              {switchItem.toCarrierName}
                            </span>
                          )}
                          <code style={{ fontSize: '0.75rem', color: 'var(--color-gray-500)', fontFamily: 'monospace' }}>
                            DOT {switchItem.toDotNumber}
                          </code>
                        </div>

                        <div style={{ fontSize: '0.75rem', color: 'var(--color-gray-600)' }}>
                          Last seen with {switchItem.fromCarrierName}: {formatDate(switchItem.lastSeenAt)}
                          <br />
                          First seen with {switchItem.toCarrierName}: {formatDate(switchItem.firstSeenAt)}
                          <br />
                          <strong>Time between: {switchItem.daysBetween} days</strong>
                        </div>
                      </div>

                      {switchItem.suspicious && switchItem.suspicionReasons.length > 0 && (
                        <div style={{
                          background: '#fef2f2',
                          borderRadius: 'var(--radius-md)',
                          padding: 'var(--space-sm)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)', marginBottom: 'var(--space-xs)' }}>
                            <AlertTriangle size={14} style={{ color: 'var(--color-danger)' }} />
                            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--color-danger)' }}>
                              Suspicious Pattern:
                            </span>
                          </div>
                          <ul style={{ margin: 0, paddingLeft: 'var(--space-lg)', fontSize: '0.75rem', color: 'var(--color-gray-700)' }}>
                            {switchItem.suspicionReasons.map((reason, ridx) => (
                              <li key={ridx}>{reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
